/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { Data, DataState, isDataLoaded, makeLoadedData } from '../../types/data-providers';
import { useData, dataFromDataset } from './DataProviderCache';
import { UserInfo } from '../../types';
import * as UserManager from '../domain/user-manager';
import { useAuthHelper } from '../../components/AuthContextProvider';
import * as ApiRequests from '../../api/requests';

export type UserDetails = {
    userInfo: Data<UserInfo>;
    updateUser: Data<any>;
    userDetailLoading: boolean;
    emailLoading: boolean;
    smsLoading: boolean;
    sessionLoading: boolean;
    updateUserInfo: Function;
    updateEmailNotification: Function;
    updateSmsNotification: Function;
    updateAutomaticLogout: Function;
};

const Context = React.createContext<UserDetails | null>(null);

function useUserInfo(): UserDetails {
    const contextState = React.useContext(Context);
    if (contextState === null) {
        throw new Error('useUserInfo must be used within a UserInfoProvider');
    }
    return contextState;
}

const UserInfoProvider: React.FC<{ userId: string }> = (props) => {
    const authHelper = useAuthHelper();
    const [updateUser, setUser] = useState<Data<any>>({ state: DataState.Loading });
    const [updateEmail, setEmail] = useState<Data<any>>({ state: DataState.Loading }); // eslint-disable-line @typescript-eslint/no-unused-vars
    const [updateSms, setSms] = useState<Data<any>>({ state: DataState.Loading }); // eslint-disable-line @typescript-eslint/no-unused-vars
    const [updateSession, setSession] = useState<Data<any>>({ state: DataState.Loading }); // eslint-disable-line @typescript-eslint/no-unused-vars
    const [userDetailLoading, setLoading] = useState(false);
    const [emailLoading, setEmailLoading] = useState(false);
    const [smsLoading, setSmsLoading] = useState(false);
    const [sessionLoading, setSessionLoading] = useState(false);
    const [userInfo, setUserInfo] = useState<Data<UserInfo>>({ state: DataState.Loading });

    const { dataset } = useData({
        cacheKey: 'user-info',
        id: props.userId,
        dataFn: React.useCallback(
            (): Promise<UserInfo> => UserManager.getUserInfo(authHelper, props.userId),
            [props.userId, authHelper]
        ),
    });
    useEffect(() => {
        setUserInfo(dataFromDataset(dataset));
    }, [dataset]);

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const updateUserInfo = async (
        userId: string,
        fName: string,
        lName: string,
        email: string,
        phoneNo: string,
        countryCode: string
    ) => {
        setLoading(true);
        setUser({
            state: DataState.Loading,
        });

        const phoneNumber = `${countryCode}${phoneNo}`;
        const result = await ApiRequests.setUserSettings(
            {
                userId,
                user: { fName: fName, lName: lName, phone: phoneNumber },
            },
            { authHelper }
        );
        if (result.ok) {
            setLoading(false);
            
            setUser({
                state: DataState.Loaded,
                value: result.response,
            });
            if (isDataLoaded(userInfo)) {
                const updatedInfo: UserInfo = {
                    ...userInfo.value,
                    user: {
                        fName: fName,
                        lName: lName,
                        email: email,
                        phone: phoneNumber,
                    },
                };

                setUserInfo(makeLoadedData(updatedInfo));
            }
        }
        if (!result.ok) {
            setLoading(false);
            setUser({ state: DataState.Error, errorMessage: ApiRequests.extractErrorCodeWithMessage(result.response) });
        }
    };
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const updateEmailNotification = async (userId: string, emailEnabled: boolean) => {
        setEmailLoading(true);

        setEmail({ state: DataState.Loading });
        const result = await ApiRequests.setUserSettings(
            {
                userId,
                notification: { emailenabled: emailEnabled },
            },
            { authHelper }
        );
        if (result.ok) {
            setEmailLoading(false);

            setEmail({
                state: DataState.Loaded,
                value: result.response,
            });
        }
        if (!result.ok) {
            setEmailLoading(false);

            setEmail({
                state: DataState.Error,
                errorMessage: ApiRequests.extractErrorCodeWithMessage(result.response),
            });
        }
    };
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const updateSmsNotification = async (userId: string, smsEnabled: boolean) => {
        setSmsLoading(true);

        setSms({ state: DataState.Loading });
        const result = await ApiRequests.setUserSettings(
            {
                userId,
                notification: { smsenabled: smsEnabled },
            },
            { authHelper }
        );
        if (result.ok) {
            setSmsLoading(false);

            setSms({
                state: DataState.Loaded,
                value: result.response,
            });
        }
        if (!result.ok) {
            setSmsLoading(false);

            setSms({
                state: DataState.Error,
                errorMessage: ApiRequests.extractErrorCodeWithMessage(result.response),
            });
        }
    };
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const updateAutomaticLogout = async (userId: string, session: boolean) => {
        setSessionLoading(true);

        setSession({ state: DataState.Loading });
        const result = await ApiRequests.setUserSettings(
            {
                userId,
                session: { sessionEnabled: session },
            },
            { authHelper }
        );
        if (result.ok) {
            setSessionLoading(false);

            setSession({
                state: DataState.Loaded,
                value: result.response,
            });
        }
        if (!result.ok) {
            setSessionLoading(false);

            setSession({
                state: DataState.Error,
                errorMessage: ApiRequests.extractErrorCodeWithMessage(result.response),
            });
        }
    };
    return (
        <Context.Provider
            value={{
                userInfo,
                updateUser,
                userDetailLoading,
                emailLoading,
                smsLoading,
                sessionLoading,
                updateUserInfo,
                updateEmailNotification,
                updateSmsNotification,
                updateAutomaticLogout,
            }}
        >
            {props.children}
        </Context.Provider>
    );
};

export { UserInfoProvider, useUserInfo };
